import Vue from 'vue';

import store from '@core/store';
import ErrorService from '@core/services/ErrorService';

import SubscriptionTypeEnum from '@constants/enums/subscription/type';

import { getCurrentSubscription } from '@packages/rate-plans/utils/currentSubscription';

import { hasAdvancedFeePlans } from '@utils/platformFeature';

import { Investor } from '@/models';

export function canViewOtherAdviserBilling(auth = null) {
    return Boolean(auth?.is_gi || (auth?.is_adviser && auth?.is_superuser));
}

export function isRootPlatformAdviser(adviser = null) {
    if (!adviser) {
        return false;
    }

    return adviser.root === true || adviser.is_root === true;
}

export function isEntitledBillableAdviser(adviser = null) {
    if (!isRootPlatformAdviser(adviser) && hasAdvancedFeePlans(adviser)) {
        return true;
    }

    return false;
}

export function getBillingPanelAdviserOptions(organisation = null, auth = null) {
    if (!canViewOtherAdviserBilling(auth) || !Array.isArray(organisation?.adviser_accounts)) {
        return [];
    }

    return organisation.adviser_accounts
        .map(account => {
            const adviserId = account?.adviser?.id || account?.adviser_id;

            if (!adviserId) {
                return null;
            }

            return {
                id: `${adviserId}`,
                name: account?.adviser?.name || `Adviser ${adviserId}`,
                adviser: account?.adviser || null
            };
        })
        .filter(Boolean)
        .filter((item, index, items) => items.findIndex(candidate => candidate.id === item.id) === index)
        .filter(option => isEntitledBillableAdviser(option.adviser));
}

export function resolveBillingPanelScopedAdviserId(organisation = null, auth = null) {
    if (canViewOtherAdviserBilling(auth)) {
        const options = getBillingPanelAdviserOptions(organisation, auth);

        if (options.length === 1) {
            return options[0].id;
        }

        return null;
    }

    return auth?.adviser_id ? `${auth.adviser_id}` : null;
}

export function resolveBillingPanelAdviserId({
    organisation = null,
    auth = null,
    selectedAdviserId = null,
    adviserOptions = null
} = {}) {
    const options = adviserOptions || getBillingPanelAdviserOptions(organisation, auth);
    const entitledOptions = options.filter(option => isEntitledBillableAdviser(option.adviser));
    const canFilterByAdviser = canViewOtherAdviserBilling(auth) && entitledOptions.length > 1;

    if (canFilterByAdviser) {
        const selectedId = selectedAdviserId ? `${selectedAdviserId}` : null;
        const selectedOption = entitledOptions.find(option => option.id === selectedId);

        return selectedOption?.id || entitledOptions[0]?.id || null;
    }

    return resolveBillingPanelScopedAdviserId(organisation, auth);
}

export function resolveAdviserForBillingContext({
    organisation = null,
    auth = null,
    adviserId = null,
    adviserOptions = null,
    adviserAccounts = null
} = {}) {
    if (!adviserId) {
        return null;
    }

    const normalisedId = `${adviserId}`;
    const options = Array.isArray(adviserOptions) ? adviserOptions : [];
    const option = options.find(entry => `${entry.id}` === normalisedId);

    if (option?.adviser) {
        return option.adviser;
    }

    const accounts = Array.isArray(adviserAccounts)
        ? adviserAccounts
        : organisation?.adviser_accounts;

    if (Array.isArray(accounts)) {
        const account = accounts.find(aa => `${aa?.adviser?.id || aa?.adviser_id}` === normalisedId);

        if (account?.adviser) {
            return account.adviser;
        }
    }

    if (auth?.adviser && `${auth.adviser.id || auth.adviser_id}` === normalisedId) {
        return auth.adviser;
    }

    return null;
}

export function hasAdvancedFeePlansForBillingContext({
    organisation = null,
    auth = null,
    adviserId = null,
    adviserOptions = null,
    adviserAccounts = null
} = {}) {
    const resolvedAdviserId = adviserId || resolveBillingPanelScopedAdviserId(organisation, auth);

    if (!resolvedAdviserId) {
        return false;
    }

    const adviser = resolveAdviserForBillingContext({
        organisation,
        auth,
        adviserId: resolvedAdviserId,
        adviserOptions,
        adviserAccounts
    });

    if (adviser && isEntitledBillableAdviser(adviser)) {
        return true;
    }

    // Investor payloads may omit adviser features; standard adviser sessions carry
    // the entitlement on auth instead.
    if (
        auth?.adviser_id &&
        `${auth.adviser_id}` === `${resolvedAdviserId}` &&
        !canViewOtherAdviserBilling(auth) &&
        !auth?.is_investor
    ) {
        return hasAdvancedFeePlans(auth?.organisation, auth?.adviser);
    }

    return false;
}

export function getSelectedBillingAdviserId() {
    const querySelector = store.getters['app/query_selector'];
    const selectedAdviserId =
        typeof querySelector === 'function' ? querySelector('investor_billing_adviser_id') : null;

    return selectedAdviserId ? `${selectedAdviserId}` : null;
}

export function resolveBillingContextAdviserAccount(organisation, auth, selectedAdviserId = null) {
    const adviserOptions = getBillingPanelAdviserOptions(organisation, auth);
    const adviserId = resolveBillingPanelAdviserId({
        organisation,
        auth,
        selectedAdviserId,
        adviserOptions
    });

    if (!adviserId || !Array.isArray(organisation?.adviser_accounts)) {
        return null;
    }

    return (
        organisation.adviser_accounts.find(
            aa => `${aa?.adviser?.id || aa?.adviser_id}` === `${adviserId}`
        ) || null
    );
}

export function hasAdvancedFeePlansForInvestorValidation(organisation = null, auth = null, selectedAdviserId = null) {
    const adviserOptions = getBillingPanelAdviserOptions(organisation, auth);
    const adviserId = resolveBillingPanelAdviserId({
        organisation,
        auth,
        selectedAdviserId: selectedAdviserId ?? getSelectedBillingAdviserId(),
        adviserOptions
    });

    return hasAdvancedFeePlansForBillingContext({
        organisation,
        auth,
        adviserId,
        adviserOptions
    });
}

export function evaluateAdviserPlanIdFromSubscriptionData(data = {}) {
    const subscriptions = Array.isArray(data.subscriptions) ? data.subscriptions : [];
    const adviserSubscription = getCurrentSubscription(subscriptions, {
        subscriptionType: SubscriptionTypeEnum.ADVISER
    });

    return adviserSubscription?.rate_plan_id || null;
}

export function extractInvestorAdviserBillingFromSubscriptionData(data = {}) {
    const billingGroupId = data.billing_group?.id || data.id || null;
    const adviserPlanId = evaluateAdviserPlanIdFromSubscriptionData(data);

    return {
        billingGroupId: billingGroupId ? `${billingGroupId}` : null,
        adviserPlanId: adviserPlanId ? `${adviserPlanId}` : null
    };
}

export function hydrateOrganisationAdviserBilling(organisation, adviserId, billing = {}) {
    if (!organisation || !adviserId || !Array.isArray(organisation.adviser_accounts)) {
        return false;
    }

    const normalisedAdviserId = `${adviserId}`;
    const account = organisation.adviser_accounts.find(
        aa => `${aa?.adviser?.id || aa?.adviser_id}` === normalisedAdviserId
    );

    if (!account) {
        return false;
    }

    const { billingGroupId, adviserPlanId } = billing;
    let updated = false;

    if (billingGroupId && account.billing_group_id !== billingGroupId) {
        Vue.set(account, 'billing_group_id', billingGroupId);
        updated = true;
    }

    if (adviserPlanId && account.adviser_rate_plan_id !== adviserPlanId) {
        Vue.set(account, 'adviser_rate_plan_id', adviserPlanId);
        updated = true;
    }

    return updated;
}

export async function fetchInvestorBillingSubscriptionContext({
    organisation = null,
    auth = null,
    selectedAdviserId = null
} = {}) {
    if (!organisation?.is_investor || !organisation?.id) {
        return null;
    }

    const adviserOptions = getBillingPanelAdviserOptions(organisation, auth);
    const adviserId = resolveBillingPanelAdviserId({
        organisation,
        auth,
        selectedAdviserId: selectedAdviserId ?? getSelectedBillingAdviserId(),
        adviserOptions
    });

    if (
        !hasAdvancedFeePlansForBillingContext({
            organisation,
            auth,
            adviserId,
            adviserOptions
        })
    ) {
        return null;
    }

    if (!adviserId) {
        return {
            adviserId: null,
            billingGroupId: null,
            adviserPlanId: null,
            hasAdviserPlan: false
        };
    }

    try {
        const response = await Investor.api.getBillingGroup(organisation.id, adviserId);
        const billing = extractInvestorAdviserBillingFromSubscriptionData(response?.data || {});

        return {
            adviserId,
            ...billing,
            hasAdviserPlan: Boolean(billing.billingGroupId || billing.adviserPlanId)
        };
    } catch (error) {
        if (error?.code === 404) {
            return {
                adviserId,
                billingGroupId: null,
                adviserPlanId: null,
                hasAdviserPlan: false
            };
        }

        ErrorService.log(error);

        return null;
    }
}

export async function resolveInvestorBillingSubscriptionValidation(options = {}) {
    const context = await fetchInvestorBillingSubscriptionContext(options);

    if (context === null) {
        return null;
    }

    return context.hasAdviserPlan;
}

export default resolveInvestorBillingSubscriptionValidation;
